import React from 'react';
import { 
  Box, Grid, Card, CardContent, Typography, Paper, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, LinearProgress, Divider,
  useTheme
} from '@mui/material';
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { getCountryName } from '../../../utils/countryUtils';
import VisitsByCountryChart from './VisitsByCountryChart';

// Цвета для визуализации
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// Компонент для отображения графика посещений по датам
export const VisitsByDateChart = ({ data }) => {
  const theme = useTheme();
  
  if (!data || data.length === 0) {
    return (
      <Card sx={{ 
        boxShadow: { xs: 1, sm: 2 },
        borderRadius: { xs: 1, sm: 2 }
      }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Нет данных о посещениях по датам
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Форматируем данные для графика
  const chartData = data.map(item => ({
    date: new Date(item.date).toLocaleDateString('ru-RU'),
    visits: item.count
  }));

  return (
    <Card sx={{ 
      boxShadow: { xs: 1, sm: 2 },
      borderRadius: { xs: 1, sm: 2 }
    }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Typography variant="h6" gutterBottom sx={{ 
          fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          Посещения по датам
        </Typography>
        <Box sx={{ height: { xs: 200, sm: 250, md: 300 }, mt: { xs: 1, sm: 2 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ 
                top: 5, 
                right: 10, 
                left: 0, 
                bottom: 5 
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 10 }}
                tickMargin={5}
              />
              <YAxis 
                tick={{ fontSize: 10 }}
                width={30}
              />
              <Tooltip 
                contentStyle={{ 
                  fontSize: '0.75rem',
                  padding: '8px',
                  borderRadius: '4px'
                }} 
              />
              <Legend 
                wrapperStyle={{ 
                  fontSize: '0.75rem',
                  paddingTop: '10px' 
                }} 
              />
              <Bar dataKey="visits" fill={theme.palette.primary.main} name="Посещения" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

// Компонент VisitsByCountryChart вынесен в отдельный файл

// Компонент для отображения графика посещений по страницам
export const VisitsByPageChart = ({ data }) => {
  const theme = useTheme();
  
  if (!data || data.length === 0) {
    return (
      <Card sx={{ 
        boxShadow: { xs: 1, sm: 2 },
        borderRadius: { xs: 1, sm: 2 }
      }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Нет данных о посещениях по страницам
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Рассчитываем общее количество посещений для процентов
  const totalVisits = data.reduce((sum, item) => sum + parseInt(item.count, 10), 0);
  
  // Форматируем данные для графика (берем только топ-10)
  const chartData = data.slice(0, 10).map(item => {
    const visits = parseInt(item.count, 10);
    const percentage = totalVisits > 0 ? ((visits / totalVisits) * 100).toFixed(1) : '0.0';
    
    return {
      page: item.path.length > 20 ? item.path.substring(0, 20) + '...' : item.path,
      fullPath: item.path, // Сохраняем полный путь для подсказки
      visits: visits,
      percentage: percentage
    };
  });

  // Используем хук для определения размера экрана
  const [windowSize, setWindowSize] = React.useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  
  React.useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Адаптивные настройки
  const useSmallScreen = windowSize.width < 600;

  return (
    <Card sx={{ 
      boxShadow: { xs: 1, sm: 2 },
      borderRadius: { xs: 1, sm: 2 }
    }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Typography variant="h6" gutterBottom sx={{ 
          fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          Топ-10 страниц
        </Typography>
        <Box sx={{ height: { xs: 250, sm: 300, md: 350 }, mt: { xs: 1, sm: 2 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ 
                top: 5, 
                right: useSmallScreen ? 10 : 30, 
                left: useSmallScreen ? 10 : 20, 
                bottom: 5 
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                type="number" 
                tick={{ fontSize: useSmallScreen ? 9 : 10 }}
                tickFormatter={(value) => value}
              />
              <YAxis 
                dataKey="page" 
                type="category" 
                width={useSmallScreen ? 80 : 120} 
                tick={{ fontSize: useSmallScreen ? 9 : 10 }}
              />
              <Tooltip 
                formatter={(value, name, props) => {
                  const percentage = props.payload.percentage;
                  return [`${value} посещений (${percentage}%)`, props.payload.fullPath];
                }}
                contentStyle={{ 
                  fontSize: '0.75rem',
                  padding: '8px',
                  borderRadius: '4px'
                }}
              />
              <Legend 
                wrapperStyle={{ 
                  fontSize: useSmallScreen ? '0.65rem' : '0.75rem',
                  paddingTop: '10px' 
                }} 
              />
              <Bar 
                dataKey="visits" 
                fill={theme.palette.primary.main} 
                name="Посещения"
                label={{
                  position: 'right',
                  formatter: (value, item) => {
                    // Проверяем, что item существует и имеет свойство percentage
                    if (item && item.payload && item.payload.percentage) {
                      return `${value} (${item.payload.percentage}%)`;
                    }
                    return `${value}`;
                  },
                  fontSize: useSmallScreen ? 9 : 10,
                  fill: theme.palette.text.primary
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

// Компонент для отображения графика посещений по устройствам
export const VisitsByDeviceChart = ({ data }) => {
  const theme = useTheme();
  
  // Отладка данных
  console.log('Данные по устройствам в компоненте:', data);
  
  if (!data || data.length === 0) {
    return (
      <Card sx={{ 
        boxShadow: { xs: 1, sm: 2 },
        borderRadius: { xs: 0, sm: 2 }
      }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Нет данных о посещениях по устройствам
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Используем хук для определения размера экрана
  const [windowSize, setWindowSize] = React.useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  
  React.useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Адаптивные размеры для круговой диаграммы
  const useSmallScreen = windowSize.width < 600;
  const outerRadius = useSmallScreen ? 60 : 80;

  // Форматируем данные для графика
  const chartData = data.map(item => ({
    name: item.deviceType || 'Неизвестно',
    value: parseInt(item.count, 10) // Убедимся, что значение числовое
  }));

  // Рассчитываем общее количество посещений для процентов
  const totalVisits = chartData.reduce((sum, item) => sum + item.value, 0);

  // Добавляем процентное соотношение к названию устройства для легенды
  const enhancedChartData = chartData.map(item => ({
    ...item,
    displayName: `${item.name}: ${((item.value / totalVisits) * 100).toFixed(1)}%`
  }));

  return (
    <Card sx={{ 
      boxShadow: { xs: 1, sm: 2 },
      borderRadius: { xs: 0, sm: 2 }
    }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Typography variant="h6" gutterBottom sx={{ 
          fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          Посещения по типам устройств
        </Typography>
        <Box sx={{ height: { xs: 250, sm: 300, md: 350 }, mt: { xs: 1, sm: 2 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={enhancedChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={null} // Убираем подписи у самой диаграммы
                outerRadius={outerRadius}
                fill={theme.palette.primary.main}
                dataKey="value"
                nameKey="name"
              >
                {enhancedChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => {
                  return [`${value} посещений (${((value / totalVisits) * 100).toFixed(1)}%)`, props.payload.name];
                }}
                contentStyle={{ 
                  fontSize: '0.75rem',
                  padding: '8px',
                  borderRadius: '4px'
                }}
              />
              <Legend 
                formatter={(value, entry) => {
                  return entry.payload.displayName;
                }}
                layout={useSmallScreen ? "horizontal" : "vertical"}
                verticalAlign={useSmallScreen ? "bottom" : "middle"}
                align={useSmallScreen ? "center" : "right"}
                wrapperStyle={{ 
                  fontSize: useSmallScreen ? '0.65rem' : '0.75rem',
                  paddingLeft: useSmallScreen ? 0 : 10,
                  maxWidth: useSmallScreen ? '100%' : '40%',
                  overflowY: 'auto'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

// Компонент VisitsByCityChart был удален, так как он дублирует функциональность CountryCityStatistics

// Компонент для отображения всех графиков
const StatisticsCharts = ({ statistics }) => {
  if (!statistics) {
    return <Typography>Загрузка данных...</Typography>;
  }

  // Проверяем, есть ли данные хотя бы в одном из разделов статистики
  const hasData = (
    (statistics.visitsByDate && statistics.visitsByDate.length > 0) ||
    (statistics.visitsByCountry && statistics.visitsByCountry.length > 0) ||
    (statistics.visitsByPage && statistics.visitsByPage.length > 0) ||
    (statistics.visitsByDevice && statistics.visitsByDevice.length > 0)
  );

  // Если данных нет вообще, показываем одно общее сообщение
  if (!hasData) {
    return (
      <Card sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Нет данных для отображения
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Посещения будут отображаться здесь после того, как пользователи начнут просматривать сайт
        </Typography>
      </Card>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <VisitsByDateChart data={statistics.visitsByDate} />
      </Grid>
      <Grid item xs={12} md={6}>
        <VisitsByCountryChart data={statistics.visitsByCountry} />
      </Grid>
      {/* Компонент VisitsByCityChart был удален, так как он дублирует функциональность CountryCityStatistics */}
      <Grid item xs={12} md={6}>
        <VisitsByPageChart data={statistics.visitsByPage} />
      </Grid>
      <Grid item xs={12} md={6}>
        <VisitsByDeviceChart data={statistics.visitsByDevice} />
      </Grid>
    </Grid>
  );
};

export default StatisticsCharts;
